// Shared display constants for the single-view region workspace.

export const ACTION_TYPES = ['basin', 'ditch', 'embankment', 'infiltration'];

// One entry per Maßnahmen type: the German label, the colour it is drawn in
// and the geometry Leaflet.Draw has to offer when the type is being added.
//
// Very little of the colour wheel is actually free here. The water level
// raster is on by default and owns blue through purple, and the building
// danger classes own yellow through dark red -- and those are areas competing
// with the two Maßnahmen that are areas themselves. Magenta is the one vivid
// hue nothing else claims, so the basin takes it.
//
// That leaves the ditch, whose only rival is the other line, the brown
// embankment. Leaflet draws polylines without a fill, so black costs nothing
// there -- as an area its 20 % fill would grey out the terrain underneath,
// but as a line it is simply the most legible thing left.
export const ACTION_META = {
  basin: {
    label: 'Auffangbecken', color: '#D81B60', geometry: 'polygon',
    idKey: 'basin_id', geometryKey: 'basin_outline',
  },
  ditch: {
    label: 'Graben', color: '#111111', geometry: 'polyline',
    idKey: 'ditch_id', geometryKey: 'ditch_points',
  },
  embankment: {
    label: 'Damm', color: '#8B4513', geometry: 'polyline',
    idKey: 'embankment_id', geometryKey: 'embankment_points',
  },
  infiltration: {
    label: 'Versickerungsfläche', color: '#4CAF50', geometry: 'polygon',
    idKey: 'infiltration_area_id', geometryKey: 'infiltration_area_outline',
  },
};

// Buildings as long as no computed scenario is active -- the blue of the old
// "Gebäude" page.
export const BUILDING_STYLE = { color: '#3388ff', fillColor: '#3388ff', fillOpacity: 0.2, weight: 2 };

// Buildings once a computed scenario is active. Class 0 ("no danger") was
// fully transparent on the old hazard-map page; here the buildings layer can
// be toggled on its own, so an invisible building would read as a missing
// one -- it is drawn white, with a grey outline so it stays legible on the
// aerial base map.
export const DANGER_CLASS_STYLES = {
  0: { color: '#757575', fillColor: '#FFFFFF', fillOpacity: 0.85, weight: 1 },
  1: { color: '#FFFF00', fillColor: '#FFFF00', fillOpacity: 0.3, weight: 2 },
  2: { color: '#FFA500', fillColor: '#FFA500', fillOpacity: 0.3, weight: 2 },
  3: { color: '#FF4500', fillColor: '#FF4500', fillOpacity: 0.3, weight: 2 },
  4: { color: '#8B0000', fillColor: '#8B0000', fillOpacity: 0.3, weight: 2 },
};

export const getDangerClassStyle = (dangerClass) =>
  DANGER_CLASS_STYLES[dangerClass] || DANGER_CLASS_STYLES[0];

// The result rasters share Leaflet's tilePane with the base maps, so their
// stacking is decided by zIndex first and DOM order second -- and both work
// against an overlay that doesn't set one. L.Control.Layers (used by
// BaseLayers) hands out zIndex 1, 2, ... to its base layers via autoZIndex,
// so "Satellit" at 2 covers an overlay left at TileLayer's default of 1; and
// switching base maps re-appends the newly selected one at the end of the
// pane, so even OpenStreetMap at a tied zIndex of 1 then wins on DOM order
// and hides the overlay for the rest of the session. Pinning the data
// overlays well above the base layers fixes both. (The Fließwege layer is
// unaffected -- leaflet-velocity draws into overlayPane, above tilePane
// entirely.)
export const WATER_LEVEL_Z_INDEX = 510;
export const DISCHARGE_Z_INDEX = 500;
// Below the two result rasters: the static flow-path mask is a solid grey
// fill, so it must never end up covering the water it sits under.
export const FLOW_PATHS_STATIC_Z_INDEX = 490;

// Text for the water level popup on the 2D and 3D maps. `value` is the raw
// depth in metres from the backend, null outside the computed area.
export const formatWaterLevel = (probe) => {
  if (probe.status === 'loading') return 'Wasserstand wird geladen…';
  if (probe.status === 'error') return 'Wasserstand konnte nicht geladen werden.';
  if (probe.value === null) return 'Außerhalb des Berechnungsgebiets';
  // Below half a centimetre the cell is dry for all practical purposes.
  if (probe.value < 0.005) return 'Wasserstand: kein Wasser';
  if (probe.value < 1) return `Wasserstand: ${Math.round(probe.value * 100)} cm`;
  return `Wasserstand: ${probe.value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} m`;
};

export const SIDEBAR_WIDTH = 92;
export const PANEL_WIDTH = 380;

// leaflet-velocity tuning for the animated Fließwege layer. Extracted
// unchanged from the old hazard-map page; see the comments there and in
// VelocityLayer.js for why these particular numbers.
export const VELOCITY_OPTIONS = {
  minVelocity: 0,
  maxVelocity: 10,
  // velocityScale is NOT a plain pixels-per-frame factor: leaflet-velocity
  // multiplies it by the map extent in radians (mapArea^0.4) and then by the
  // projection's pixels-per-radian, so the usable magnitude is tiny. 0.00019
  // gives a top-speed particle ~12 px/frame at zoom 16, which is the fastest
  // the smoothed flow directions can carry without throwing particles off
  // the metre-wide flow band (where the field is 0 and they freeze).
  velocityScale: 0.00019,
  // Frames a particle is followed before it is re-seeded somewhere random --
  // ~300 frames at ~56 fps is a 5 s run, long enough to follow a channel
  // clear across the viewport.
  particleAge: 300,
  // windy.js gates on `delta > 1000/frameRate` against an integer-millisecond
  // clock, so 60 yields ~56 fps on a 60 Hz display and still ~60 on a 120 Hz
  // one, which keeps the speed roughly hardware-independent.
  frameRate: 60,
  // Density dial. Low because particles concentrate themselves: off-band ones
  // are recycled within ~3 frames while on-band ones live the full
  // particleAge.
  particleMultiplier: 1 / 32,
  lineWidth: 2,
  colorScale: ['#FFFF00', '#FFA500', '#FF4500', '#8B0000'],
};
